import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react';
import { Layer, Rect, Stage } from 'react-konva';
import useImage from 'use-image';
import Konva from 'konva';
import {
  Image,
  Text,
  useSlideEditorContext
} from 'providers/SlideEditorProvider';

export interface IProps {
  layerRef: React.RefObject<Konva.Layer>;
  setShowModalStyle: Dispatch<SetStateAction<boolean>>;
  addTextOnSlide: (text: Text) => void;
  addImageOnSlide: (imageData: Image) => void;
  bgImagePosition: { x: number; y: number };
  setbgImagePosition: Dispatch<SetStateAction<{ x: number; y: number }>>;
  setbgImageScale: Dispatch<SetStateAction<number>>;
}

const SlideEditor = ({
  layerRef,
  setShowModalStyle,
  addTextOnSlide,
  addImageOnSlide,
  bgImagePosition,
  setbgImagePosition,
  setbgImageScale
}: IProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { slides, activeSlideIndex, updateBackground } =
    useSlideEditorContext();
  const slide = slides[activeSlideIndex];
  const [image] = useImage(slide.background);
  const [stageSize, setStageSize] = useState({
    width: window.innerWidth <= 600 ? window.innerWidth : 600,
    height: window.innerHeight
  });
  const scaleFactor = (stageSize.height - 40) / (image?.height || 1);
  const resizedWidth = (image?.width || 0) * scaleFactor;

  useEffect(() => {
    setbgImageScale(scaleFactor);
  }, [scaleFactor]);

  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current) return;
      setStageSize({
        width:
          window.innerWidth <= 600 ? containerRef.current?.offsetWidth : 600,
        height: window.innerHeight
      });
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      setShowModalStyle(false);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (updateBackground) {
      setbgImagePosition({ x: 0, y: 0 });
      return;
    }

    setbgImagePosition({
      x: slide.backgroundPosition?.x || 0,
      y: slide.backgroundPosition?.y || 0
    });

    const nodes = [...slide.texts, ...slide.images].sort(
      (a, b) => a.zIndex - b.zIndex
    );

    addNodesOnSlide(nodes);
  }, [activeSlideIndex, slides]);

  const addNodesOnSlide = async (nodes: (Text | Image)[]) => {
    for (const node of nodes) {
      if ('text' in node) {
        await addTextOnSlide(node);
      } else {
        await addImageOnSlide(node);
      }
    }
  };

  const dragEdgesLimit = (pos: { x: number; y: number }) => {
    // Calculate the boundaries for the x-coordinate
    const minX = Math.min(0, stageSize.width - resizedWidth);
    const maxX = Math.max(0, stageSize.width - resizedWidth);
    // Limit the x position so that the left or right edges of the image align with the rect edges
    const limitedX = Math.max(minX, Math.min(pos.x, maxX));
    return { x: limitedX, y: 0 }; // Limit the y-coordinate
  };

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
    >
      <Stage
        width={stageSize.width}
        height={stageSize.height}
        style={{ backgroundColor: 'black' }}
      >
        <Layer ref={layerRef}>
          <Rect
            x={bgImagePosition.x}
            y={bgImagePosition.y}
            width={resizedWidth}
            height={stageSize.height - 40}
            fillPatternImage={image}
            fillPatternRepeat="no-repeat"
            draggable={true}
            dragBoundFunc={dragEdgesLimit}
            fillPatternScale={{
              x: scaleFactor,
              y: scaleFactor
            }}
            onDragEnd={e =>
              setbgImagePosition({
                x: e.target.x(),
                y: e.target.y()
              })
            }
          />
        </Layer>
      </Stage>
    </div>
  );
};

export default SlideEditor;
